/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * LayoutStylePicker
 *
 * Three clickable thumbnail cards showing miniature layout previews.
 * Reads from layoutStore, calls setLayoutStyle on click for instant preview.
 * Reusable: can be embedded in any settings page.
 */

import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { useLayoutStore } from '@/stores/layoutStore';
import type { LayoutStyle } from '@/config/module-registry';

const LAYOUT_OPTIONS: { value: LayoutStyle; previewId: string }[] = [
  { value: 'dropdown', previewId: 'dropdown' },
  { value: 'rail', previewId: 'rail' },
  { value: 'topbar', previewId: 'topbar' },
];

export function LayoutStylePicker() {
  const { t } = useTranslation('common');
  const { layoutStyle, setLayoutStyle, useModuleLogos, setUseModuleLogos } = useLayoutStore();

  // Only show the logo toggle for rail and topbar (dropdown uses logos in its dropdown panel already)
  const showLogoToggle = layoutStyle === 'rail' || layoutStyle === 'topbar';

  return (
    <div className="lsp-container">
      <div className="lsp-header">
        <h3 className="lsp-title">{t('layoutSettings.title')}</h3>
        <p className="lsp-description">{t('layoutSettings.description')}</p>
      </div>

      <div className="lsp-grid">
        {LAYOUT_OPTIONS.map(({ value, previewId }) => {
          const isSelected = layoutStyle === value;
          return (
            <button
              key={value}
              className={`lsp-card ${isSelected ? 'selected' : ''}`}
              onClick={() => setLayoutStyle(value)}
              type="button"
            >
              {/* Miniature preview */}
              <div className="lsp-preview">
                <LayoutPreview type={previewId} />
              </div>

              {/* Label */}
              <div className="lsp-label">
                <span className="lsp-name">{t(`layoutSettings.${value}.name`)}</span>
                <span className="lsp-desc">{t(`layoutSettings.${value}.description`)}</span>
              </div>

              {/* Checkmark */}
              {isSelected && (
                <div className="lsp-check">
                  <Check size={14} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Logo vs Icon toggle */}
      {showLogoToggle && (
        <div className="lsp-logo-toggle">
          <label className="lsp-toggle-row">
            <div className="lsp-toggle-text">
              <span className="lsp-toggle-label">{t('layoutSettings.useModuleLogos')}</span>
              <span className="lsp-toggle-hint">{t('layoutSettings.useModuleLogosHint')}</span>
            </div>
            <button
              type="button"
              className={`lsp-switch ${useModuleLogos ? 'on' : ''}`}
              onClick={() => setUseModuleLogos(!useModuleLogos)}
            >
              <span className="lsp-switch-thumb" />
            </button>
          </label>
        </div>
      )}

      <style>{styles}</style>
    </div>
  );
}

/** Tiny CSS-only layout previews */
function LayoutPreview({ type }: { type: string }) {
  if (type === 'rail') {
    return (
      <div className="lsp-mini lsp-mini-rail">
        <div className="lsp-mini-rail-bar">
          <div className="lsp-mini-dot active" />
          <div className="lsp-mini-dot" />
          <div className="lsp-mini-dot" />
        </div>
        <div className="lsp-mini-sidebar">
          <div className="lsp-mini-line w70" />
          <div className="lsp-mini-line w50" />
          <div className="lsp-mini-line w60" />
        </div>
        <div className="lsp-mini-content" />
      </div>
    );
  }

  if (type === 'topbar') {
    return (
      <div className="lsp-mini lsp-mini-topbar">
        <div className="lsp-mini-side">
          <div className="lsp-mini-tabs">
            <div className="lsp-mini-tab active" />
            <div className="lsp-mini-tab" />
            <div className="lsp-mini-tab" />
          </div>
          <div className="lsp-mini-lines">
            <div className="lsp-mini-line w70" />
            <div className="lsp-mini-line w50" />
            <div className="lsp-mini-line w60" />
          </div>
        </div>
        <div className="lsp-mini-content" />
      </div>
    );
  }

  // dropdown (default)
  return (
    <div className="lsp-mini lsp-mini-dropdown">
      <div className="lsp-mini-sidebar">
        <div className="lsp-mini-dropdown-hdr" />
        <div className="lsp-mini-line w70" />
        <div className="lsp-mini-line w50" />
        <div className="lsp-mini-line w60" />
      </div>
      <div className="lsp-mini-content" />
    </div>
  );
}

const styles = `
  .lsp-container {
    margin-bottom: 24px;
  }

  .lsp-header {
    margin-bottom: 16px;
  }

  .lsp-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 0 0 4px;
  }

  .lsp-description {
    font-size: 12px;
    color: var(--color-text-muted);
    margin: 0;
  }

  .lsp-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  @media (max-width: 640px) {
    .lsp-grid {
      grid-template-columns: 1fr;
    }
  }

  .lsp-card {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px;
    background: var(--color-bg-primary);
    border: 2px solid var(--color-border-subtle);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: left;
  }

  .lsp-card:hover {
    border-color: var(--color-border-default);
    background: var(--color-bg-hover);
  }

  .lsp-card.selected {
    border-color: var(--color-brand);
    box-shadow: 0 0 0 1px var(--color-brand);
  }

  .lsp-check {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--color-brand);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .lsp-label {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .lsp-name {
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .lsp-desc {
    font-size: 10px;
    color: var(--color-text-muted);
    line-height: 1.3;
  }

  /* Miniature preview boxes */
  .lsp-preview {
    height: 64px;
    border-radius: 6px;
    overflow: hidden;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
  }

  .lsp-mini {
    width: 100%;
    height: 100%;
    display: flex;
  }

  /* Shared mini elements */
  .lsp-mini-line {
    height: 4px;
    border-radius: 2px;
    background: var(--color-border-default);
    margin-bottom: 4px;
  }

  .lsp-mini-line.w70 { width: 70%; }
  .lsp-mini-line.w50 { width: 50%; }
  .lsp-mini-line.w60 { width: 60%; }

  .lsp-mini-content {
    flex: 1;
    background: var(--color-bg-primary);
  }

  .lsp-mini-sidebar {
    width: 36%;
    padding: 6px 4px;
    border-right: 1px solid var(--color-border-subtle);
  }

  /* Rail preview */
  .lsp-mini-rail {
    display: flex;
  }

  .lsp-mini-rail-bar {
    width: 14px;
    padding: 6px 2px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    border-right: 1px solid var(--color-border-subtle);
  }

  .lsp-mini-dot {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    background: var(--color-border-default);
  }

  .lsp-mini-dot.active {
    background: var(--color-brand);
  }

  .lsp-mini-rail .lsp-mini-sidebar {
    width: 30%;
  }

  /* Topbar preview */
  .lsp-mini-topbar {
    display: flex;
  }

  .lsp-mini-side {
    width: 36%;
    display: flex;
    flex-direction: column;
    border-right: 1px solid var(--color-border-subtle);
  }

  .lsp-mini-tabs {
    display: flex;
    gap: 2px;
    padding: 4px 2px;
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .lsp-mini-tab {
    flex: 1;
    height: 6px;
    border-radius: 2px;
    background: var(--color-border-default);
  }

  .lsp-mini-tab.active {
    background: var(--color-brand);
  }

  .lsp-mini-lines {
    padding: 6px 4px;
  }

  /* Dropdown preview */
  .lsp-mini-dropdown-hdr {
    height: 10px;
    border-radius: 3px;
    background: var(--color-border-default);
    margin-bottom: 6px;
  }

  /* Logo vs Icon toggle */
  .lsp-logo-toggle {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid var(--color-border-subtle);
  }

  .lsp-toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    cursor: pointer;
  }

  .lsp-toggle-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .lsp-toggle-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .lsp-toggle-hint {
    font-size: 10px;
    color: var(--color-text-muted);
  }

  .lsp-switch {
    position: relative;
    width: 36px;
    height: 20px;
    border-radius: 10px;
    border: none;
    background: var(--color-border-default);
    cursor: pointer;
    transition: background 0.2s ease;
    flex-shrink: 0;
    padding: 0;
  }

  .lsp-switch.on {
    background: var(--color-brand);
  }

  .lsp-switch-thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
    transition: transform 0.2s ease;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  }

  .lsp-switch.on .lsp-switch-thumb {
    transform: translateX(16px);
  }
`;
